import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import SectionHeading from "@/components/base/SectionHeading";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { warehouseFeatures, warehouseOptions } from "@/mocks/warehouse";
import { contactInfo } from "@/mocks/site";
import { HERO_IMAGES } from "@/config/heroImages";

const warehouseFaqs = [
  {
    q: "Can I store my household goods at your warehouse?",
    a: "Yes. We store household goods, office assets, furniture and valuable belongings at our own Bangalore warehouse, packed safely before intake and recorded on an item-wise storage receipt.",
  },
  {
    q: "How secure is the warehouse?",
    a: "The facility has controlled entry, round-the-clock security, waterproof RCC flooring, raised pallets and scheduled pest treatment, so your goods stay dry, dust-free and protected.",
  },
  {
    q: "What is the minimum storage duration?",
    a: "We accept storage from a few weeks to several months. Both short term and long term options are available, and you can extend at any time.",
  },
  {
    q: "Do you pack the goods before storing them?",
    a: "Yes. All items are professionally packed with cartons, foam, bubble wrap and wooden crates before intake, so they remain protected for the entire storage period.",
  },
  {
    q: "Can I visit the warehouse before booking?",
    a: "Absolutely. We welcome site visits during working hours at our Kannahalli warehouse, so you can inspect the bays and see exactly where your goods will be kept.",
  },
  {
    q: "Can you deliver my goods back from storage?",
    a: "Yes. Give us a date with prior notice and we deliver your goods straight to your new home or office anywhere in Bangalore, Mysore or Tumkur.",
  },
];

export default function WarehousePage() {
  return (
    <>
      <PageHero
        eyebrow="Warehouse Facility"
        title="Warehouse Facility in Bangalore"
        subtitle="Modern, spacious and secure warehousing at our own Bangalore hub, for household goods, office assets and valuable belongings."
        crumbs={[{ label: "Home", href: "/" }, { label: "Warehouse Facility" }]}
        image={HERO_IMAGES.warehouse}
      />

      {/* Overview */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto grid max-w-screen-2xl grid-cols-1 items-start gap-10 px-4 md:px-6 lg:grid-cols-2 lg:gap-14">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Our Facility"
              title="Storage that protects, not just stores"
            />
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              <p>
                Lahari Packers and Movers operates its own warehouse in Bangalore, which means your goods never pass
                through a third party facility. Our bays are built to prevent water seepage and are kept dry, dust free
                and treated against pests on a fixed schedule.
              </p>
              <p>
                Every item is professionally packed before intake, stacked on pallets instead of the floor and
                recorded on an item-wise storage receipt. When you are ready, we deliver straight to your new home or
                office anywhere in Bangalore, Mysore or Tumkur.
              </p>
            </div>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {["Own facility, no third parties", "Covered loading dock", "Wide truck access aisles", "Same-day retrieval possible"].map(
                (item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" aria-hidden="true" />
                    {item}
                  </li>
                ),
              )}
            </ul>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href={contactInfo.phoneHref}
                data-cta="Book Storage Now"
                data-cta-location="warehouse-intro"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-phone-fill text-lg" aria-hidden="true" /> Book Storage Now
              </a>
              <Link
                to="/services/storage"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-6 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                <i className="ri-store-3-line text-lg" aria-hidden="true" /> Storage Service Details
              </Link>
              <a
                href={contactInfo.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-direction-line text-lg" aria-hidden="true" /> Get Directions
              </a>
            </div>
          </div>

          <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-200">
            <img
              src="/uploads/warehouse-office.jpeg"
              alt="Lahari Packers and Movers warehouse and operations office in Bangalore"
              title="Lahari Warehouse and Storage Bangalore"
              className="h-full w-full object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Facility Features"
            title="Built to keep your goods exactly as they left home"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {warehouseFeatures.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Storage options */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading eyebrow="Storage Options" title="Choose how you want to store" />
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {warehouseOptions.map((option) => (
              <div key={option.title} className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className={`${option.icon} text-2xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{option.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{option.text}</p>
                <ul className="mt-4 space-y-2">
                  {option.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line mt-0.5 text-primary-500" aria-hidden="true" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location map */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Find Us"
            title="Visit our warehouse in Bangalore"
            subtitle="Come see the facility for yourself. We are located at Anikethana Nagara, Kannahalli, Bengaluru and welcome site visits during working hours."
          />
          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
            <div className="lg:col-span-2">
              <div className="relative overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <iframe
                  title="Lahari Packers and Movers warehouse location map, Anikethana Nagara, Kannahalli Bengaluru"
                  src={contactInfo.mapEmbed}
                  className="h-[320px] w-full md:h-[420px]"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
                <div className="absolute inset-x-3 bottom-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50/95 px-3 py-2.5 backdrop-blur-sm md:inset-x-4 md:bottom-4">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-foreground-950 md:text-sm">
                    <i className="ri-map-pin-2-fill text-lg text-primary-600" aria-hidden="true" /> Anikethana Nagara, Kannahalli
                  </span>
                  <a
                    href={contactInfo.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-direction-line" aria-hidden="true" /> Get Directions
                  </a>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">Warehouse address</h3>
              <ul className="mt-5 space-y-4 text-sm text-foreground-600">
                <li className="flex gap-3">
                  <i className="ri-map-pin-2-line mt-0.5 text-lg text-primary-500" aria-hidden="true" />
                  <span>{contactInfo.address}</span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-time-line mt-0.5 text-lg text-primary-500" aria-hidden="true" />
                  <span>
                    <strong className="font-semibold text-foreground-800">Mon to Sat:</strong> 9:30 AM – 6:30 PM
                    <br />
                    <strong className="font-semibold text-foreground-800">Sunday:</strong> 10:30 AM – 2:30 PM
                  </span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-phone-line mt-0.5 text-lg text-primary-500" aria-hidden="true" />
                  <a href={contactInfo.phoneHref} className="transition-colors hover:text-primary-600">
                    {contactInfo.phone}
                  </a>
                </li>
              </ul>
              <a
                href={contactInfo.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-5 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                <i className="ri-navigation-line text-lg" aria-hidden="true" /> Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
            <div>
              <SectionHeading
                align="left"
                eyebrow="Good to Know"
                title="Warehouse questions, answered"
                subtitle="A few things customers ask us before booking storage. Still unsure? Call us any time."
              />
              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-base font-semibold text-foreground-950">Still have a question?</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  Our helpline is open 24 x 7 for storage advice, facility visits and shifting support.
                </p>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={contactInfo.phoneHref}
                    className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-phone-fill text-lg" aria-hidden="true" /> Call Now
                  </a>
                  <a
                    href={contactInfo.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#25D366] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1EBE5B]"
                  >
                    <i className="ri-whatsapp-line text-lg" aria-hidden="true" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
            <FaqAccordion items={warehouseFaqs} />
          </div>
        </div>
      </section>

      <CtaBanner
        title="Need secure storage in Bangalore?"
        description="Tell us the volume and duration and we will suggest the right bay, packing plan and delivery schedule."
      />
    </>
  );
}